import {observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, CheckBox, Column, Row, Text} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {ShowVMenu, VMenuItem} from "react-vmenu";
import {ErrorBoundary, ES} from "web-vcore";
import {liveSkin} from "../../../../Utils/Styles/StyleManager.js";
import {CloneSafe} from "../../../../Utils/General/Serialization.js";
import {Vec2} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {PropertiesUI_SharedProps} from "../PropertiesUI.js";
import {ObjModifier} from "../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {MapState} from "../../../../Store/main/maps/@MapState.js";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {GetObjModifierPanelsToShow, objModifierClasses} from "../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/@ModifierRegistry.js";
import {ModifyString} from "js-vextensions";

export const Modifiers_CompUI = observer_mgl((props: {} & PropertiesUI_SharedProps)=>{
	const {map, mapState, obj, Change} = props;

	const [selectedModifierIndex, setSelectedModifierIndex] = useState(0);
	// find... by index (if in-bounds); else 0 (KeepBetween clamps to the last entry when the list shrinks)
	const selectedModifierIndex_final = selectedModifierIndex.KeepBetween(0, (obj.modifiers ?? []).length - 1);

	return (
		<Column>
			<Row>
				<Text>Modifiers:</Text>
				<Row style={{flexWrap: "wrap", gap: 5, marginLeft: 5}}>
					{(obj.modifiers ?? []).map((modifier, index)=>{
						const modifierTypeName = ModifyString(
							Object.keys(modifier).filter(a=>a != "enabled")[0],
							m=>[m.startLower_to_upper, m.lowerUpper_to_lowerSpaceLower],
						);
						return <Button key={index} p="2px 5px" text={modifierTypeName}
							style={ES(
								selectedModifierIndex_final == index && {backgroundColor: "rgba(100,200,100,.5)"},
							)}
							onClick={()=>setSelectedModifierIndex(index)}/>;
					})}
					<Button mdIcon="plus" onClick={e=>{
						const buttonRect = (e.target as HTMLElement).getBoundingClientRect();
						ShowVMenu(
							{pos: new Vec2(buttonRect.left, buttonRect.top + buttonRect.height)},
							<>
							{objModifierClasses.map(modType=>(
								<VMenuItem key={modType.name} text={modType.name} style={liveSkin.Style_VMenuItem()} onClick={()=>{
									const newModifier = new ObjModifier({[modType.key]: new modType.mod({})});
									Change(a=>a.modifiers = (a.modifiers ?? []).concat(newModifier));
								}}/>
							))}
							</>,
						);
					}}/>
				</Row>
			</Row>
			{selectedModifierIndex_final <= (obj.modifiers ?? []).length - 1 &&
				<ModifierEditorUI key={selectedModifierIndex_final} {...{map, mapState, obj, index: selectedModifierIndex_final, Change}}/>}
		</Column>
	);
});

export type ModifierUI_SharedProps = {
	map: DMap;
	mapState: MapState;
	obj: MapObject;
	modWrapper: ObjModifier;
	ChangeObj: (objChanger: (obj: MapObject)=>any)=>void;
	ChangeModWrapper: (mutator: (mod: ObjModifier)=>any)=>void;
};
const ModifierEditorUI = observer_mgl((props: {index: number} & PropertiesUI_SharedProps)=>{
	const {map, mapState, obj, Change: ChangeObj, index} = props;
	const mod = obj.modifiers[index];
	const modType = objModifierClasses.find(a=>a.key in mod);
	if (modType == null) return <Text>Unknown modifier type</Text>;
	const ChangeModWrapper = (mutator: (mod: ObjModifier)=>any)=>{
		return ChangeObj(a=>mutator(a.modifiers[index]));
	};

	const sharedProps = {map, mapState, obj, modWrapper: mod, ChangeObj, ChangeModWrapper} as ModifierUI_SharedProps;
	return (
		<Column mt={5} style={{background: "rgba(255,255,255,.2)", padding: 5, borderRadius: 5}}>
			<Row>
				<Row center>{modType.name}</Row>
				<Row ml="auto">
					<CheckBox text="Enabled" value={mod.enabled} onChange={val=>ChangeModWrapper(a=>a.enabled = val)}/>
					<Button ml={5} mdIcon="content-copy" title="Clone" onClick={()=>{
						ChangeObj(a=>{
							const thisModifier_new = CloneSafe(a.modifiers[index]);
							const thisModifier_clone = CloneSafe(thisModifier_new);
							a.modifiers[index] = thisModifier_new;
							a.modifiers.Insert(index + 1, thisModifier_clone);
						});
					}}/>
					<Button ml={5} mdIcon="arrow-up" title="Move up" enabled={index > 0} onClick={()=>{
						ChangeObj(a=>a.modifiers.Move(a.modifiers[index], index - 1));
					}}/>
					<Button ml={5} mdIcon="arrow-down" title="Move down" enabled={index < obj.modifiers.length - 1} onClick={()=>{
						ChangeObj(a=>a.modifiers.Move(a.modifiers[index], index + 1));
					}}/>
					<Button ml={5} mdIcon="delete" onClick={()=>{
						ShowMessageBox({
							title: "Delete modifier?", cancelButton: true,
							message: "Are you sure you want to delete this modifier?",
							onOK: ()=>{
								ChangeObj(a=>a.modifiers.RemoveAt(index));
							},
						});
					}}/>
				</Row>
			</Row>
			<ErrorBoundary>
				{GetObjModifierPanelsToShow(mod).map((PanelClass, panelIndex)=>{
					return <PanelClass key={panelIndex} {...sharedProps}/>;
				})}
			</ErrorBoundary>
		</Column>
	);
});